import React, { useState, useRef, useCallback } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../loading/spinner";
import ImageCropper from "./imageCropper";
import { getAllDays } from "./clinic_function/addClinic_function";
import { toggleState } from "../../specialMethod/toggleState";

// Helper functions for localStorage operations
const getDoctorData = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    return {};
  }
};

const getClinics = () => {
  try {
    return JSON.parse(localStorage.getItem("clinics")) || [];
  } catch {
    return [];
  }
};

const saveClinics = (clinics) => {
  localStorage.setItem("clinics", JSON.stringify(clinics));
};

const generateId = () => "clinic_" + Date.now() + "_" + Math.random().toString(36).substring(2, 8);

const AddClinic = ({ closeAlertClinicBanner, value, updatedClinics }) => {
  const [state, setState] = useState({
    loadingSpinner: false,
    image: "",
    currentPage: false,
  });

  const inputRef = useRef();

  // Get doctor data (for specialization, etc.)
  const doctorData = getDoctorData();
  const isDentist = doctorData?.specialization?.[0]?.toLowerCase() === "dentist";

  // For editing: pre-fill values if updatedClinics is provided
  const initialValues = updatedClinics
    ? {
        clinicName: updatedClinics.clinicName || "",
        location: updatedClinics.location || "",
        startTime: updatedClinics.startTime || "",
        endTime: updatedClinics.endTime || "",
        incharge: updatedClinics.incharge || "",
        clinicContact: updatedClinics.clinicContact || "",
        clinicNewFees: updatedClinics.clinicNewFees || "",
        clinicOldFees: updatedClinics.clinicOldFees || "",
        days: updatedClinics.days || [],
        dropdownOpen: false,
        // Dentist fields (if any)
        generalDentistry: updatedClinics.generalDentistry || 0,
        cosmeticDentistry: updatedClinics.cosmeticDentistry || 0,
        dentalCheckUp: updatedClinics.dentalCheckUp || 0,
        restorationAndFilling: updatedClinics.restorationAndFilling || 0,
        crownsAndBridges: updatedClinics.crownsAndBridges || 0,
        fractureAndTrauma: updatedClinics.fractureAndTrauma || 0,
        vaneersAndLaminates: updatedClinics.vaneersAndLaminates || 0,
        extraction: updatedClinics.extraction || 0,
        oralAndMaxillofacialSurgery: updatedClinics.oralAndMaxillofacialSurgery || 0,
        orthodonticsTreatment: updatedClinics.orthodonticsTreatment || 0,
        childDentistry: updatedClinics.childDentistry || 0,
        implant: updatedClinics.implant || 0,
        scalingAndTeethCleaning: updatedClinics.scalingAndTeethCleaning || 0,
        rootCanalTreatment: updatedClinics.rootCanalTreatment || 0,
      }
    : {
        clinicName: "",
        location: "",
        startTime: "",
        endTime: "",
        incharge: "",
        clinicContact: "",
        clinicNewFees: "",
        clinicOldFees: "",
        days: [],
        dropdownOpen: false,
        generalDentistry: 0,
        cosmeticDentistry: 0,
        dentalCheckUp: 0,
        restorationAndFilling: 0,
        crownsAndBridges: 0,
        fractureAndTrauma: 0,
        vaneersAndLaminates: 0,
        extraction: 0,
        oralAndMaxillofacialSurgery: 0,
        orthodonticsTreatment: 0,
        childDentistry: 0,
        implant: 0,
        scalingAndTeethCleaning: 0,
        rootCanalTreatment: 0,
      };

  // Formik
  const formik = useFormik({
    initialValues,
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: Yup.object({
      clinicName: Yup.string().required("Enter clinic name"),
      location: Yup.string().required("Enter location"),
      startTime: Yup.string().required("Enter start time"),
      endTime: Yup.string().required("Enter end time"),
      incharge: Yup.string().required("Enter incharge name"),
      clinicContact: Yup.string()
        .matches(/^\d{10}$/, "Must be 10 digits")
        .required("Enter mobile number"),
      days: Yup.array().min(1, "Select at least one day"),
    }),
    onSubmit: (values) => {
      addClinic(values);
    },
  });

  // Image selection
  const handleImageChange = useCallback((event) => {
    if (event.target.files?.length) {
      const reader = new FileReader();
      reader.onload = () => {
        toggleState("image", reader.result, setState);
        toggleState("currentPage", true, setState);
      };
      reader.readAsDataURL(event.target.files[0]);
    }
  }, []);

  // Crop handlers
  const onCropDone = useCallback(
    (croppedImage) => {
      toggleState("image", croppedImage, setState);
      toggleState("currentPage", false, setState);
    },
    []
  );

  const onCropCancel = useCallback(() => {
    toggleState("image", "", setState);
    toggleState("currentPage", false, setState);
  }, []);

  // Main submit: add or update clinic in localStorage
  const addClinic = useCallback(
    (values) => {
      setState((prev) => ({ ...prev, loadingSpinner: true }));

      // Get existing clinics
      const clinics = getClinics();
      let updatedClinicsList;

      // Prepare clinic object
      const clinicData = {
        ...values,
        image: state.image || "",
        doctorEmail: doctorData.email || "",
        createdAt: new Date().toISOString(),
      };

      if (updatedClinics) {
        // Update existing clinic (find by id)
        const index = clinics.findIndex((c) => c.id === updatedClinics.id);
        if (index !== -1) {
          clinics[index] = { ...clinics[index], ...clinicData, updatedAt: new Date().toISOString() };
          updatedClinicsList = clinics;
          toast.success("Clinic updated successfully!");
        } else {
          toast.error("Clinic not found.");
          setState((prev) => ({ ...prev, loadingSpinner: false }));
          return;
        }
      } else {
        // New clinic
        const newClinic = {
          id: generateId(),
          ...clinicData,
        };
        updatedClinicsList = [...clinics, newClinic];
        toast.success("Clinic added successfully!");
      }

      saveClinics(updatedClinicsList);

      // Close modal and refresh parent
      if (closeAlertClinicBanner) closeAlertClinicBanner();
      if (value) value(); // close modal

      setState((prev) => ({ ...prev, loadingSpinner: false }));
    },
    [state.image, updatedClinics, doctorData, closeAlertClinicBanner, value]
  );

  // Toggle dropdown for days
  const handleToggleDropDown = useCallback(() => {
    formik.setFieldValue("dropdownOpen", !formik.values.dropdownOpen);
  }, [formik]);

  const handleDaySelection = useCallback(
    (day) => {
      const updatedDays = formik.values.days.includes(day)
        ? formik.values.days.filter((d) => d !== day)
        : [...formik.values.days, day];
      formik.setFieldValue("days", updatedDays);
    },
    [formik]
  );

  // Define form fields dynamically (common fields)
  const formFields = [
    { name: "clinicName", label: "Clinic Name", type: "text", placeholder: "Clinic Name" },
    { name: "location", label: "Location", type: "text", placeholder: "Location" },
    { name: "startTime", label: "Start Time", type: "time", placeholder: "" },
    { name: "endTime", label: "End Time", type: "time", placeholder: "" },
    { name: "incharge", label: "Incharge", type: "text", placeholder: "Incharge Name" },
    { name: "clinicContact", label: "Clinic Contact", type: "tel", placeholder: "10-digit mobile" },
    { name: "clinicNewFees", label: "New Patient Fees", type: "number", placeholder: "Fees" },
    { name: "clinicOldFees", label: "Old Patient Fees", type: "number", placeholder: "Fees" },
  ];

  // Dentist-specific service fields
  const dentistFields = [
    { name: "generalDentistry", label: "General Dentistry", type: "number" },
    { name: "cosmeticDentistry", label: "Cosmetic Dentistry", type: "number" },
    { name: "dentalCheckUp", label: "Dental Check-up", type: "number" },
    { name: "restorationAndFilling", label: "Restoration & Filling", type: "number" },
    { name: "crownsAndBridges", label: "Crowns & Bridges", type: "number" },
    { name: "fractureAndTrauma", label: "Fracture & Trauma", type: "number" },
    { name: "vaneersAndLaminates", label: "Vaneers & Laminates", type: "number" },
    { name: "extraction", label: "Extraction", type: "number" },
    { name: "oralAndMaxillofacialSurgery", label: "Oral & Maxillofacial Surgery", type: "number" },
    { name: "orthodonticsTreatment", label: "Orthodontics Treatment", type: "number" },
    { name: "childDentistry", label: "Child Dentistry", type: "number" },
    { name: "implant", label: "Implant", type: "number" },
    { name: "scalingAndTeethCleaning", label: "Scaling & Teeth Cleaning", type: "number" },
    { name: "rootCanalTreatment", label: "Root Canal Treatment", type: "number" },
  ];

  // Filter out new/old fees for dentist
  const getCommonFields = () => {
    if (isDentist) {
      return formFields.filter((f) => f.name !== "clinicNewFees" && f.name !== "clinicOldFees");
    }
    return formFields;
  };

  return (
    <>
      <div
        id="login-popup"
        className="bg-black/50 fixed inset-0 flex items-center justify-center z-50"
      >
        <div className="h-[80%] md:h-[92%] overflow-y-auto">
          <div className="w-full max-w-6xl">
            <div className="bg-white rounded-lg shadow p-5 relative">
              <button
                type="button"
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-900"
                onClick={value}
              >
                <span aria-hidden="true">&times;</span>
              </button>

              <div className="text-center">
                <h2 className="text-2xl font-semibold">
                  {updatedClinics ? "Edit Clinic" : "Clinic Details"}
                </h2>
              </div>
              <hr className="my-1" />

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  formik.handleSubmit();
                }}
              >
                {isDentist ? (
                  <>
                    <div className="grid grid-cols-3 gap-6">
                      {/* Image Upload */}
                      <div className="col-span-3 sm:col-span-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Prescription Photo
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          ref={inputRef}
                          onChange={handleImageChange}
                          style={{ display: "none" }}
                        />
                        <button
                          type="button"
                          className="text-blue-600"
                          onClick={() => inputRef.current.click()}
                        >
                          {state.image ? "Image Selected" : "Choose Image"}
                        </button>
                      </div>

                      {/* Common Fields */}
                      {getCommonFields().map(({ name, label, type, placeholder }) => (
                        <div key={name} className="col-span-3 sm:col-span-1">
                          <label className="block text-sm font-medium text-gray-700">
                            {label}
                          </label>
                          <input
                            type={type}
                            name={name}
                            placeholder={placeholder}
                            value={formik.values[name]}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className="w-full px-4 py-2 border rounded-lg focus:border-blue-500"
                          />
                          {formik.touched[name] && formik.errors[name] && (
                            <p className="text-red-500 text-sm">{formik.errors[name]}</p>
                          )}
                        </div>
                      ))}

                      {/* Days Selection */}
                      <div className="col-span-3 sm:col-span-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Select Days
                        </label>
                        <button
                          type="button"
                          className="text-gray-700 bg-white border border-gray-400 rounded-lg px-6 py-2"
                          onClick={handleToggleDropDown}
                        >
                          {formik.values.days.length} Days
                        </button>
                        {formik.values.dropdownOpen && (
                          <ul className="mt-2 p-3 border rounded bg-white">
                            {getAllDays.map((day, idx) => (
                              <li key={idx}>
                                <label className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    checked={formik.values.days.includes(day)}
                                    onChange={() => handleDaySelection(day)}
                                  />
                                  <span>{day}</span>
                                </label>
                              </li>
                            ))}
                          </ul>
                        )}
                        {formik.touched.days && formik.errors.days && (
                          <p className="text-red-500 text-sm">{formik.errors.days}</p>
                        )}
                      </div>
                    </div>

                    {/* Dentist Service & Charges */}
                    <div className="font-semibold my-1">Service & Charges</div>
                    <div className="grid grid-cols-3 gap-6 p-2 rounded-lg my-2">
                      {dentistFields.map(({ name, label, type }) => (
                        <div key={name} className="flex border border-gray-200 py-2">
                          <span className="text-gray-500 ml-1">{label}</span>
                          <span className="ml-auto text-gray-900 border-l border-gray-800">
                            <input
                              type={type}
                              name={name}
                              placeholder="00"
                              value={formik.values[name]}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              className="w-10 ml-2"
                            />
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-2 gap-6">
                    {/* Image Upload */}
                    <div className="col-span-2 sm:col-span-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Prescription Photo
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        ref={inputRef}
                        onChange={handleImageChange}
                        style={{ display: "none" }}
                      />
                      <button
                        type="button"
                        className="text-blue-600"
                        onClick={() => inputRef.current.click()}
                      >
                        {state.image ? "Image Selected" : "Choose Image"}
                      </button>
                    </div>

                    {/* Common Fields */}
                    {formFields.map(({ name, label, type, placeholder }) => (
                      <div key={name} className="col-span-2 sm:col-span-1">
                        <label className="block text-sm font-medium text-gray-700">
                          {label}
                        </label>
                        <input
                          type={type}
                          name={name}
                          placeholder={placeholder}
                          value={formik.values[name]}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="w-full px-4 py-2 border rounded-lg focus:border-blue-500"
                        />
                        {formik.touched[name] && formik.errors[name] && (
                          <p className="text-red-500 text-sm">{formik.errors[name]}</p>
                        )}
                      </div>
                    ))}

                    {/* Days Selection */}
                    <div className="col-span-2 sm:col-span-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Select Days
                      </label>
                      <button
                        type="button"
                        className="text-gray-700 bg-white border border-gray-400 rounded-lg px-6 py-2"
                        onClick={handleToggleDropDown}
                      >
                        {formik.values.days.length} Days
                      </button>
                      {formik.values.dropdownOpen && (
                        <ul className="mt-2 p-3 border rounded bg-white">
                          {getAllDays.map((day, idx) => (
                            <li key={idx}>
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={formik.values.days.includes(day)}
                                  onChange={() => handleDaySelection(day)}
                                />
                                <span>{day}</span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      )}
                      {formik.touched.days && formik.errors.days && (
                        <p className="text-red-500 text-sm">{formik.errors.days}</p>
                      )}
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-lg py-2 mt-4"
                >
                  {updatedClinics ? "Update" : "Submit"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {state.currentPage && (
        <ImageCropper
          image={state.image}
          onCropDone={onCropDone}
          onCropCancel={onCropCancel}
        />
      )}
      {state.loadingSpinner && <Spinner />}
      <ToastContainer />
    </>
  );
};

export default AddClinic;